/**
 * @file ResetPasswordScreen.jsx
 * @description Handles the password-reset completion flow. Shown when Supabase
 *   fires a PASSWORD_RECOVERY auth event (user clicked the reset-password email
 *   link). Collects and validates a new password, calls updateUser, then clears
 *   recovery mode so the user lands on their normal view.
 * @module components/ResetPasswordScreen
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { supabase } from "../lib/supabase";
import { CheckIcon, KeyIcon, EyeIcon, EyeOffIcon } from "./Icons";
import Logo from "./Logo";

/**
 * @param {Object} props
 * @param {Function} props.onDone - Called after password is successfully updated.
 *   Parent should clear recoveryMode so the normal app view is restored.
 */
const PW_RULES_KEYS = [
  { test: pw => pw.length >= 8,      key: "auth.req8chars" },
  { test: pw => /[A-Z]/.test(pw),    key: "auth.reqUppercase" },
  { test: pw => /[a-z]/.test(pw),    key: "auth.reqLowercase" },
  { test: pw => /\d/.test(pw),       key: "auth.reqNumber" },
];

export default function ResetPasswordScreen({ onDone }) {
  const { t } = useTranslation();
  const [pw, setPw]           = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);

  const allRulesMet = PW_RULES_KEYS.every(r => r.test(pw));

  async function handleSubmit() {
    setError("");
    if (!allRulesMet) { setError(t('resetPassword.errorRequirements')); return; }
    if (pw !== confirm) { setError(t('resetPassword.errorMismatch')); return; }

    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: pw });
      if (err) { setError(err.message); return; }
      setSuccess(true);
      // Give the user a moment to read the success message then clear recovery mode
      setTimeout(() => onDone(), 2000);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      background: T.bg, padding: "40px 20px",
    }}>
      <div style={{
        width: "100%", maxWidth: 400,
        background: T.surface,
        borderRadius: 16,
        border: `1px solid ${T.border}`,
        padding: "36px 32px",
        boxShadow: T.shadow,
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Logo variant="lockup" height={32} alt="ABAcases" />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: "-0.02em", marginBottom: 6 }}>
            {t('resetPassword.title')}
          </div>
          <div style={{ fontSize: 13, color: T.textLight, lineHeight: 1.5 }}>
            {t('resetPassword.subtitle')}
          </div>
        </div>

        {success ? (
          <div style={{
            padding: "14px 16px",
            background: `${T.green}14`,
            border: `1px solid ${T.green}40`,
            borderRadius: 10,
            fontSize: 14, color: T.green, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <CheckIcon size={16} color={T.green} />
            {t('resetPassword.success')}
          </div>
        ) : (
          <>
            {/* New password */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 6 }}>{t('resetPassword.newPasswordLabel')}</div>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <span style={{ position: "absolute", left: 12, color: T.textLight, pointerEvents: "none" }}>
                  <KeyIcon size={14} />
                </span>
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="••••••••"
                  value={pw}
                  autoFocus
                  onChange={e => { setPw(e.target.value); setError(""); }}
                  style={{
                    width: "100%", padding: "10px 44px 10px 36px",
                    border: `1.5px solid ${T.border}`, borderRadius: 10,
                    fontSize: 14, color: T.text, background: "#fff",
                    fontFamily: T.fontBody, outline: "none", boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  onMouseDown={() => setShowPw(true)}
                  onMouseUp={() => setShowPw(false)}
                  onMouseLeave={() => setShowPw(false)}
                  onTouchStart={() => setShowPw(true)}
                  onTouchEnd={() => setShowPw(false)}
                  style={{
                    position: "absolute", right: 8, background: "none", border: "none",
                    cursor: "pointer", padding: 6, display: "flex", alignItems: "center",
                    color: showPw ? T.blue : T.textLight,
                  }}
                >
                  {showPw ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                </button>
              </div>

              {/* Strength checklist */}
              {pw.length > 0 && (
                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 12px" }}>
                  {PW_RULES_KEYS.map(r => {
                    const met = r.test(pw);
                    return (
                      <div key={r.key} style={{
                        display: "flex", alignItems: "center", gap: 5,
                        fontSize: 11, color: met ? T.green : T.textLight,
                        fontWeight: met ? 600 : 400, transition: "color 0.15s",
                      }}>
                        {met
                          ? <CheckIcon size={11} color={T.green} />
                          : <span style={{ width: 11, height: 11, borderRadius: "50%", border: `1.5px solid ${T.border}`, display: "inline-block", flexShrink: 0 }} />
                        }
                        {t(r.key)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 6 }}>{t('resetPassword.confirmPasswordLabel')}</div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.textLight, pointerEvents: "none" }}>
                  <KeyIcon size={14} />
                </span>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); setError(""); }}
                  style={{
                    width: "100%", padding: "10px 12px 10px 36px",
                    border: `1.5px solid ${confirm && confirm !== pw ? T.red : T.border}`,
                    borderRadius: 10,
                    fontSize: 14, color: T.text, background: "#fff",
                    fontFamily: T.fontBody, outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>
              {confirm && confirm !== pw && (
                <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>{t('resetPassword.errorMismatch')}</div>
              )}
            </div>

            {error && (
              <div style={{
                marginBottom: 14, padding: "10px 14px",
                background: `${T.red}12`, border: `1px solid ${T.red}30`,
                borderRadius: 10, fontSize: 13, color: T.red, lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting || !allRulesMet || pw !== confirm}
              style={{
                width: "100%", padding: "11px 0",
                background: T.blue, color: "#fff",
                border: "none", borderRadius: 10,
                fontSize: 14, fontWeight: 700,
                fontFamily: T.fontBody,
                cursor: (submitting || !allRulesMet || pw !== confirm) ? "not-allowed" : "pointer",
                opacity: (submitting || !allRulesMet || pw !== confirm) ? 0.5 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {submitting ? t('resetPassword.buttonLoading') : t('resetPassword.button')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
